import { useMemo, type ComponentProps } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import { usePortfolioQuery } from "@/v2-backoffice-client/portfolio/portfolio.query"
import type { PortfolioQuery } from "@/v2-backoffice-client/portfolio/__types__/portfolio.query"
import { usePortfolioFilters } from "@/v2-backoffice-client/portfolio/portfolio.filters"
import {
  derivePortfolioCustomers,
  derivePortfolioFleet,
  filterPortfolioCustomers,
  filterPortfolioSummaries,
} from "@/v2-backoffice-client/portfolio/portfolio.utils"
import { derivePortfolioRiskCounts } from "@/v2-backoffice-client/portfolio/portfolio.health.utils"
import PortfolioFilterBar from "@/v2-backoffice-client/portfolio/PortfolioFilterBar"
import PortfolioSubTabs from "@/v2-backoffice-client/portfolio/PortfolioSubTabs"

export type PortfolioTabProps = ComponentProps<"div">

const EMPTY_PORTFOLIO: PortfolioQuery["getUsagePortfolio"] = []

// Portfolio: the book-of-business lens on the Activity page. This component
// owns the book itself — the portfolio query, the global filter bar, and the
// scoping derivations every view shares; the sub-tab views (and the daily
// time-series query they share) live in PortfolioSubTabs. All derived
// client-side from the same functions the per-customer Activity tabs use, so
// this view and the panels always agree. Observations only: CRM workflow
// stays in HubSpot.
function PortfolioTab(props: PortfolioTabProps) {
  const { className, ...divProps } = props

  const {
    filters,
    setCustomerIds,
    setExcludeCustomerIds,
    setExcludeUserIds,
    setOwners,
    setChurnLevels,
    setScoreLevels,
    setLicenseStatusCodes,
    clearFilters,
  } = usePortfolioFilters()

  const { data, error, loading } = usePortfolioQuery()

  const portfolio = data?.getUsagePortfolio ?? EMPTY_PORTFOLIO

  // The one place memoisation earns its keep: derivePortfolioCustomers
  // summarises every member of every account in the book (churn, score,
  // plays' inputs) and sits above every sub-tab, and `filters` is kept
  // identity-stable by usePortfolioFilters for exactly this chain. A
  // facet click re-filters the summaries; it does not re-derive them.
  const scopedRows = useMemo(
    () => filterPortfolioCustomers(portfolio, filters),
    [portfolio, filters],
  )
  const scopedCustomers = useMemo(
    () => derivePortfolioCustomers(scopedRows),
    [scopedRows],
  )
  const customers = useMemo(
    () => filterPortfolioSummaries(scopedCustomers, filters),
    [scopedCustomers, filters],
  )
  // Standard facet behavior for the donut: every OTHER filter narrows its
  // counts, only the churn selection itself is left out (the component dims
  // deselected levels instead, so they stay clickable).
  const riskCounts = useMemo(
    () =>
      derivePortfolioRiskCounts(
        filterPortfolioSummaries(scopedCustomers, {
          churnLevels: [],
          scoreLevels: filters.scoreLevels,
          licenseStatusCodes: filters.licenseStatusCodes,
        }),
      ),
    [scopedCustomers, filters.scoreLevels, filters.licenseStatusCodes],
  )

  const fleet = useMemo(() => derivePortfolioFleet(customers), [customers])

  const hasData = customers.length > 0

  if (loading && portfolio.length === 0) {
    return <p className="text-sm text-muted-foreground">Loading portfolio…</p>
  }

  if (portfolio.length === 0) {
    return (
      <div className={cn("flex flex-col gap-2", className)} {...divProps}>
        <ErrorMessage error={error} />
        <p className="text-xs text-muted-foreground">
          No customer-linked usage yet — the portfolio fills in once customers
          have seats with tracked activity.
        </p>
      </div>
    )
  }

  return (
    <div
      className={cn(
        "portfolio-tab flex h-full min-h-0 flex-col gap-2",
        className,
      )}
      {...divProps}
    >
      <ErrorMessage error={error} />

      <PortfolioFilterBar
        customers={portfolio}
        filters={filters}
        scopedCustomerCount={customers.length}
        scopedSeatCount={fleet.seatCount}
        onCustomerIdsChange={setCustomerIds}
        onExcludeCustomerIdsChange={setExcludeCustomerIds}
        onExcludeUserIdsChange={setExcludeUserIds}
        onOwnersChange={setOwners}
        onChurnLevelsChange={setChurnLevels}
        onScoreLevelsChange={setScoreLevels}
        onLicenseStatusCodesChange={setLicenseStatusCodes}
        onClear={clearFilters}
      />

      {!hasData && (
        <p className="text-xs text-muted-foreground">
          The current filters leave no customers — clear or loosen them.
        </p>
      )}

      {hasData && (
        <PortfolioSubTabs
          customers={customers}
          fleet={fleet}
          riskCounts={riskCounts}
          filters={filters}
          onChurnLevelsChange={setChurnLevels}
        />
      )}
    </div>
  )
}

export default PortfolioTab
